import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {DropdownForm} from "../../base/dropdown/dropdown";
import {DatePickerForm} from "../../base/date-picker";
import Button from "../../base/button/button";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import {InputForm} from "../../base/input/input";
import Api from "../../../../core/services/api/api";
import {Close} from "@mui/icons-material";
import useIndustry from "../../../hooks/use-industry";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import MultiSelectPartSearchSection from "../../text-fields/autocomplete/part-autocomplete/multi-select-part-search-section";

const types = [
    {id: -1, title: 'All'},
    {id: 1, title: 'Add'},
    {id: 2, title: 'Remove'},
]

const formValues = {
    keyword: 'keyword',
    category: 'category',
    type: 'type',
    reason: 'reason',
    startDate: 'startDate',
    endDate: 'endDate',
    industry: 'industry',
}

const schema = Yup.object().shape({
    [formValues.keyword]: Yup.string().nullable(),
    [formValues.type]: Yup.object().nullable(),
    [formValues.industry]: Yup.object().nullable(),
    [formValues.category]: Yup.object().nullable(),
    [formValues.reason]: Yup.object().nullable(),
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const InventoryAdjustmentRecordSearchSection = ({
                                                    singleProduct,
                                                    searchData,
                                                    defaultProduct,
                                                }) => {
    const [reasons, setReasons] = useState([]);
    const [parts, setParts] = useState([]);
    const isMounted = useIsMounted();

    const {cachedIndustry, industries, categoriesOfFetchedIndustry, fetchedIndustry, fetchIndustry, hasNoIndustries} = useIndustry();
    const categories = useMemo(() => categoriesOfFetchedIndustry
            ?.filter(category => category.parentId === null)
            ?.map(e => ({id: e.id, title: e.title})) ?? []
        , [categoriesOfFetchedIndustry]);

    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();
    const valueRef = useRef();

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * Calls the api for getting the list of reasons from server
     * @return {Promise<void>}
     */
    const getReasons = useCallback(async () => {
        const response = await Api.getInventoryAdjustmentsReasons()
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setReasons(response?.data ?? [])
        }
    }, [isMounted]);

    /**
     * With initial render of the component, Calls the function for getting the list of all reasons
     */
    useEffect(() => {
        getReasons().then();
    }, [getReasons])


    /**
     * Memo version of initial values
     * @type {object}
     */
    const initialValues = useMemo(() => {
        let parsedCategories = (JSON.parse(searchData.filters?.categories ?? '[]')?.map(e => e.id) ?? []);
        if (!Array.isArray(parsedCategories)) {
            parsedCategories = [parsedCategories];
        }
        let parsedReasons = (JSON.parse(searchData.filters?.reason ?? '[]')?.map(e => e.id) ?? []);
        if (!Array.isArray(parsedReasons)) {
            parsedReasons = [parsedReasons];
        }
        return {
            [formValues.reason]: reasons?.filter(e => parsedReasons.includes(e.id)),
            [formValues.type]: types.find(e => e.id === searchData.filters?.type) ?? types[0],
            [formValues.category]: categories?.filter(e => parsedCategories.includes(e.id)),
            [formValues.keyword]: searchData.filters?.keyword,
            [formValues.startDate]: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : undefined,
            [formValues.endDate]: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : undefined,
            [formValues.industry]: searchData.filters?.industry ?? fetchedIndustry,
        }
    }, [searchData.filters?.categories, searchData.filters?.reason, searchData.filters?.keyword, searchData.filters?.startDate, searchData.filters?.endDate, searchData.filters?.industry, searchData.filters?.type, reasons, categories, fetchedIndustry])

    /**
     * With initial render of the components:
     * - if the industry is enabled, fetch the industry from the server.
     */
    useEffect(() => {
        if (searchData.filters?.industry?.id) {
            return;
        }
        fetchIndustry({industryId: searchData.filters?.industry?.id}).then()
    }, [searchData.filters?.industry, fetchIndustry]);

    /**
     * Sets the initial values of the customers in the state.
     */
    useEffect(() => {
        setParts(searchData.parts);
    }, [searchData.parts]);

    /**
     * Submits the changes in the search section by changing the query parameters in the url
     * @param values
     */
    const onSubmit = (values) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            industry: values[formValues.industry],
            startDate: values?.[formValues.startDate] ? DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values?.[formValues.endDate] ? DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            keyword: values?.[formValues.keyword],
            type: values[formValues.type]?.id,
            reason: !!values?.[formValues.reason] ? JSON.stringify(values[formValues.reason]) : undefined,
            categories: !!values?.[formValues.category] ? JSON.stringify(values[formValues.category]) : undefined,
        }))
        searchData.setParts(parts);
    }

    /**
     * Handles removing the selected category from the list when remove icon button clicked on input chips
     * @param event
     * @param category
     * @private
     */
    const _removeSelectedCategory = (event, category) => {
        const isLastItem = (valueRef.current?.category?.length ?? 0) < 2;
        formRef.current.change(formValues.category, isLastItem
            ? undefined
            : valueRef.current?.category?.filter(e => e.id !== category.id)
        );
        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Renders the category select options
     * @param item
     * @param index
     * @param length
     * @return {JSX.Element}
     */
    const renderSelectedCategoriesValue = (item, index, length) => {
        return index < 2
            ? <Button key={item.id ?? item.label ?? item.name ?? item.title} onClick={(e) => _removeSelectedCategory(e, item)}
                      className={'multi-select-input-chips'}>
                <div className={'remove-icon-container'}>
                    <Close className='icon extraSmall'/>
                </div>
                <p>
                    {item.label ?? item.name ?? item.title ?? ''}
                </p>
            </Button>
            : index === (length - 1)
                ? <div key={item.id ?? item.label ?? item.name ?? item.title} style={{height: '26px'}}
                       className={'d-flex align-items-center justify-content-center'}>
                    <p className={'text-sm font-weight-regular'}>
                        {`+${length - 2} more`}
                    </p>
                </div>
                : <></>
    }

    /**
     * Handles removing the selected reason from the list when remove icon button clicked on input chips
     * @param event
     * @param reason
     * @private
     */
    const _removeSelectedReason = (event, reason) => {
        const isLastItem = (valueRef.current?.reason?.length ?? 0) < 2;
        formRef.current.change(formValues.reason, isLastItem
            ? undefined
            : valueRef.current?.reason?.filter(e => e.id !== reason.id)
        );
        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Renders the category select options
     * @param item
     * @param index
     * @param length
     * @return {JSX.Element}
     */
    const renderSelectedReasonsValue = (item, index, length) => {
        return index < 2
            ? <Button
                key={item.id}
                onClick={(e) => _removeSelectedReason(e, item)}
                className={'multi-select-input-chips'}
            >
                <div className={'remove-icon-container'}>
                    <Close className='icon extraSmall'/>
                </div>
                <p>
                    {`${item.code} - ${item.title}`}
                </p>
            </Button>
            : index === (length - 1)
                ? <div key={item.id ?? item.label ?? item.name ?? item.title} style={{height: '26px'}}
                       className={'d-flex align-items-center justify-content-center'}>
                    <p className={'text-sm font-weight-regular'}>
                        {`+${length - 2} more`}
                    </p>
                </div>
                : <></>
    }

    /**
     * Empties the sub-category values of a particular category that has been changed.
     * @param e
     * @param form
     */
    const onIndustryChange = (e, form) => {
        form.change(formValues.category, []);
        fetchIndustry({industryId: e.target.value?.id}).then();
    }

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            className={'d-flex w-100 align-items-center justify-content-center'}
            render={({values, form}) => {
                formRef.current = form;
                valueRef.current = values;
                return (
                    <Row className='d-flex w-100 align-items-center justify-content-center'>
                        {
                            !singleProduct &&
                            <Col xs={12} className='mx-2 mb-3 align-items-center justify-content-center flex-wrap'>
                                <MultiSelectPartSearchSection
                                    includeDefaultPart={singleProduct}
                                    parts={parts}
                                    setParts={setParts}
                                    defaultSelectedProduct={defaultProduct}
                                />
                            </Col>
                        }
                        <Col xs={12} className={'mb-3'}>
                            <InputForm
                                onChange={(val) => form.change('keyword', val)}
                                value={values[formValues.keyword] ?? ''}
                                label={'Keyword'}
                                name={formValues.keyword}
                                type={'text'}
                                required={required[formValues.keyword]}
                            />
                        </Col>
                        {
                            !singleProduct &&
                            <>
                                {
                                    industries.length > 1 &&
                                    <Col xs={12} md={6} className='px-2 mb-3'>
                                        <DropdownForm
                                            label={'Industry'}
                                            name={'industry'}
                                            required={required.industry}
                                            data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                            onChange={(e) => onIndustryChange(e, form)}
                                        />
                                    </Col>
                                }
                                {
                                    !hasNoIndustries &&
                                    <Col xs={12} md={6} className={'mb-3'}>
                                        <DropdownForm
                                            renderMultipleSelectValue={renderSelectedCategoriesValue}
                                            padding={'3px 15px'}
                                            label={'Category'}
                                            className={'py-0'}
                                            name={formValues.category}
                                            required={false}
                                            multiple
                                            data={categories.map(e => ({
                                                value: e,
                                                label: e.title,
                                                id: e.id,
                                            })) ?? []}
                                        />
                                    </Col>
                                }
                            </>
                        }
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DropdownForm
                                padding={'3px 15px'}
                                label={'Type'}
                                className={'py-0'}
                                name={formValues.type}
                                required={false}
                                data={types.map(e => ({
                                    value: e,
                                    label: e.title,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DropdownForm
                                padding={'3px 15px'}
                                label={'Reasons'}
                                className={'py-0'}
                                name={formValues.reason}
                                required={false}
                                renderMultipleSelectValue={renderSelectedReasonsValue}
                                multiple
                                data={reasons.map(e => ({
                                    value: e,
                                    label: `${e.code} - ${e.title}`,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.startDate}
                                label={'Start Date'}
                                required={required[formValues.startDate]}
                                maxDate={values[formValues.endDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.endDate}
                                label={'End Date'}
                                required={required[formValues.endDate]}
                                minDate={values[formValues.startDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12}
                             className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                {
                                    searchData.loading
                                        ? "Searching..."
                                        : "Search"
                                }
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default InventoryAdjustmentRecordSearchSection;
